import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { registerAudio } from '../lib/audioManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Wordmark from '../components/Wordmark';
import {
  SpeakerHigh,
  BookOpen,
  Play,
  Check,
  X,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
  SpinnerGap,
  CreditCard,
  House,
  Tag,
  Heart,
  Eye,
  EyeSlash,
  Crown,
  CaretDown
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVEL_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1'];

const PlacementTestPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [results, setResults] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [determinedLevel, setDeterminedLevel] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioCache, setAudioCache] = useState({});
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutPassword, setCheckoutPassword] = useState('');
  const [showCheckoutPassword, setShowCheckoutPassword] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [showSubPlans, setShowSubPlans] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponFree, setCouponFree] = useState(false);
  const [couponError, setCouponError] = useState('');
  const audioRef = useRef(null);

  const applyCoupon = (rawCode) => {
    const code = (rawCode ?? '').trim();
    if (!code) {
      setCouponApplied(false);
      setCouponFree(false);
      setCouponError('');
      return;
    }
    if (code === '26954') {
      setCouponApplied(true); setCouponFree(false); setCouponError('');
    } else if (code.toUpperCase() === 'ELEGIDO') {
      setCouponApplied(true); setCouponFree(true); setCouponError('');
    } else {
      setCouponApplied(false); setCouponFree(false);
      setCouponError('Ungültiger Gutscheincode');
    }
  };

  useEffect(() => {
    loadContent();
    return () => { stopAudio(); };
  }, []);

  const loadContent = async () => {
    try {
      const res = await axios.get(`${API}/placement-test/content`);
      setContent(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Tests');
    } finally { setLoading(false); }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
  };

  const playAudio = async () => {
    if (isPlaying) { stopAudio(); return; }

    const level = LEVEL_ORDER[currentLevel];
    const levelContent = content[level];
    if (!levelContent) return;

    if (!startTime) setStartTime(Date.now());

    // Check cache first
    if (audioCache[level]) {
      const audio = registerAudio(new Audio(audioCache[level]));
      audioRef.current = audio;
      setIsPlaying(true);
      audio.onended = () => { setIsPlaying(false); audioRef.current = null; };
      audio.onerror = () => { setIsPlaying(false); audioRef.current = null; };
      audio.play().catch(() => setIsPlaying(false));
      return;
    }

    setIsPlaying(true);
    setAudioLoading(true);
    try {
      const res = await axios.post(`${API}/placement-test/tts`, { text: levelContent.audio_text, level });
      setAudioLoading(false);
      if (res.data.audio_base64) {
        const mimeType = res.data.format === 'm4a' ? 'audio/mp4' : 'audio/mp3';
        const dataUrl = `data:${mimeType};base64,${res.data.audio_base64}`;
        setAudioCache(prev => ({ ...prev, [level]: dataUrl }));
        const audio = registerAudio(new Audio(dataUrl));
        audioRef.current = audio;
        audio.onended = () => { setIsPlaying(false); audioRef.current = null; };
        audio.onerror = () => { setIsPlaying(false); audioRef.current = null; };
        audio.play().catch(() => setIsPlaying(false));
      } else { setIsPlaying(false); }
    } catch (e) {
      console.error('TTS error:', e);
      toast.error('Audio konnte nicht geladen werden');
      setIsPlaying(false);
      setAudioLoading(false);
    }
  };

  const goToLevel = (idx) => {
    if (idx < 0 || idx >= LEVEL_ORDER.length) return;
    stopAudio();
    setCurrentLevel(idx);
    if (!startTime) setStartTime(Date.now());
  };

  const handleResponse = (understood) => {
    stopAudio();
    const level = LEVEL_ORDER[currentLevel];
    const timeTaken = startTime ? (Date.now() - startTime) / 1000 : 0;
    const newResults = [...results.filter(r => r.level !== level), { level, understood, time_taken: timeTaken }];
    setResults(newResults);

    if (!understood || currentLevel >= LEVEL_ORDER.length - 1) {
      completeTest(newResults);
    } else {
      setCurrentLevel(currentLevel + 1);
      setStartTime(null);
    }
  };

  const calculateLevelClient = (finalResults) => {
    const order = ['A1', 'A2', 'B1', 'B2', 'C1'];
    let best = 'A1';
    for (const r of finalResults) {
      if (r.understood && order.includes(r.level)) {
        if (order.indexOf(r.level) > order.indexOf(best)) best = r.level;
      }
    }
    return best;
  };

  const completeTest = async (finalResults) => {
    // Always compute locally as fallback so user never sees an error
    const fallbackLevel = calculateLevelClient(finalResults);
    try {
      const res = await axios.post(`${API}/placement-test/submit`, { results: finalResults }, { withCredentials: true });
      setDeterminedLevel(res.data?.determined_level || fallbackLevel);
    } catch (error) {
      console.error('Placement submit failed, using local level:', error);
      setDeterminedLevel(fallbackLevel);
    } finally {
      setTestComplete(true);
      setShowPricing(true);
    }
  };

  const handleSubscribe = async () => {
    // Authenticated users → normal checkout (with coupon support)
    if (user) {
      setProcessingPayment(true);
      try {
        const res = await axios.post(`${API}/payments/create-checkout`, {
          package_id: 'basic',
          origin_url: window.location.origin,
          coupon_code: couponApplied ? (couponFree ? 'ELEGIDO' : '26954') : ''
        }, { withCredentials: true });
        if (res.data.url) {
          toast.success('Weiterleitung zur sicheren Zahlung...');
          window.location.href = res.data.url;
        }
      } catch (error) {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Fehler beim Starten des Zahlungsvorgangs');
      } finally { setProcessingPayment(false); }
      return;
    }
    // Guest flow: collect name + email + password and use public-checkout
    if (!checkoutName.trim() || !checkoutEmail.trim()) {
      setCheckoutError('Bitte gib deinen Namen und deine E-Mail ein');
      toast.error('Bitte fülle zuerst deinen Namen und deine E-Mail aus');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(checkoutEmail)) {
      setCheckoutError('Bitte gib eine gültige E-Mail ein');
      toast.error('Bitte gib eine gültige E-Mail ein');
      return;
    }
    if (!checkoutPassword || checkoutPassword.length < 6) {
      setCheckoutError('Bitte wähle ein Passwort mit mindestens 6 Zeichen');
      toast.error('Bitte wähle ein Passwort mit mindestens 6 Zeichen');
      return;
    }
    setProcessingPayment(true);
    setCheckoutError('');
    const payload = {
      name: checkoutName.trim(),
      email: checkoutEmail.trim().toLowerCase(),
      password: checkoutPassword,
      origin_url: window.location.origin,
      spanish_level: determinedLevel || 'A2',
      coupon_code: couponApplied ? (couponFree ? 'ELEGIDO' : '26954') : ''
    };
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await axios.post(`${API}/payments/public-checkout`, payload, { timeout: 30000 });
        if (res.data.url) {
          toast.success(res.data.free ? 'Zugang freigeschaltet! Weiterleitung...' : 'Weiterleitung zur sicheren Zahlung...');
          window.location.href = res.data.url;
          return;
        }
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          const msg = typeof detail === 'string' ? detail : 'Fehler beim Starten der Zahlung.';
          setCheckoutError(msg);
          toast.error(msg);
          setProcessingPayment(false);
          return;
        }
        if (attempt < 3) await new Promise(r => setTimeout(r, 800 * attempt));
      }
    }
    const detail = lastErr?.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : 'Verbindung langsam. Bitte noch einmal versuchen.';
    setCheckoutError(msg);
    toast.error(msg);
    setProcessingPayment(false);
  };

  /** Recurring subscription handler — shared between guest & logged-in
   * flows. Guests are routed through /payments/public-subscription which
   * creates the account first; logged-in users skip straight to
   * /payments/create-subscription. */
  const handleSubscribeRecurring = async (packageId) => {
    setProcessingPayment(true);
    setCheckoutError('');
    try {
      if (user) {
        const res = await axios.post(`${API}/payments/create-subscription`, {
          package_id: packageId,
          origin_url: window.location.origin,
        }, { withCredentials: true });
        if (res.data.url) {
          toast.success('Weiterleitung zur sicheren Zahlung...');
          try {
            const price = { sub_1m: 100, sub_3m: 80, sub_6m: 60 }[packageId] || 0;
            const { trackEvent } = await import('../utils/metaPixel');
            trackEvent('InitiateCheckout', {
              content_ids: [packageId],
              content_type: 'subscription',
              content_name: { sub_1m: '1 Monat', sub_3m: '3 Monate', sub_6m: '6 Monate' }[packageId],
              currency: 'EUR',
              value: price,
              num_items: 1,
            });
            await new Promise(r => setTimeout(r, 250));
          } catch { /* pixel disabled */ }
          window.location.href = res.data.url;
        }
        return;
      }
      // Guest path — same validation as one-off public-checkout
      if (!checkoutName.trim() || !checkoutEmail.trim()) {
        const m = 'Bitte gib zuerst Namen und E-Mail ein';
        setCheckoutError(m); toast.error(m); setProcessingPayment(false); return;
      }
      if (!/\S+@\S+\.\S+/.test(checkoutEmail)) {
        const m = 'Bitte gib eine gültige E-Mail ein';
        setCheckoutError(m); toast.error(m); setProcessingPayment(false); return;
      }
      if (!checkoutPassword || checkoutPassword.length < 6) {
        const m = 'Bitte wähle ein Passwort mit mindestens 6 Zeichen';
        setCheckoutError(m); toast.error(m); setProcessingPayment(false); return;
      }
      const res = await axios.post(`${API}/payments/public-subscription`, {
        name: checkoutName.trim(),
        email: checkoutEmail.trim().toLowerCase(),
        password: checkoutPassword,
        package_id: packageId,
        origin_url: window.location.origin,
        spanish_level: determinedLevel || 'A2',
      }, { timeout: 30000 });
      if (res.data.url) {
        toast.success('Weiterleitung zur sicheren Zahlung...');
        try {
          const price = { sub_1m: 100, sub_3m: 80, sub_6m: 60 }[packageId] || 0;
          const { trackEvent } = await import('../utils/metaPixel');
          trackEvent('InitiateCheckout', {
            content_ids: [packageId],
            content_type: 'subscription',
            content_name: { sub_1m: '1 Monat', sub_3m: '3 Monate', sub_6m: '6 Monate' }[packageId],
            currency: 'EUR',
            value: price,
            num_items: 1,
          });
          await new Promise(r => setTimeout(r, 250));
        } catch { /* pixel disabled */ }
        window.location.href = res.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Fehler beim Starten des Abos';
      setCheckoutError(msg);
      toast.error(msg);
      setProcessingPayment(false);
    }
  };

  const buyLevel = (targetLevel) => {
    stopAudio();
    setDeterminedLevel(targetLevel);
    setShowPricing(true);
    setTestComplete(true);
  };

  if (loading) return (
    <div className="min-h-screen canvas-spain flex items-center justify-center">
      <SpinnerGap size={40} className="animate-spin text-[#AB1C24]" />
    </div>
  );

  // Pricing View
  if (showPricing) return (
    <div className="min-h-screen canvas-spain py-12 px-4">
      <div className="max-w-lg mx-auto">
        {determinedLevel && determinedLevel === 'C1' && (
          <div className="bg-gradient-to-br from-[#6B8E23] to-[#4e6b14] rounded-2xl p-8 text-white text-center mb-8" data-testid="c1-ready-card">
            <GraduationCap size={48} className="mx-auto mb-4" weight="fill" />
            <h2 className="font-heading text-2xl font-bold mb-2">¡Enhorabuena! Nivel C1</h2>
            <p className="text-white/95 mb-2">Dein Spanisch ist auf C1-Niveau — du bist bereit, das C1-Programm zu starten.</p>
            <p className="text-white/80 text-sm">Im Kurs vertiefst du Ausdruck, Nuancen und fließendes Sprechen auf hohem Niveau.</p>
          </div>
        )}
        {determinedLevel && determinedLevel !== 'C1' && (
          <div className="bg-gradient-to-br from-[#AB1C24] to-[#8A151C] rounded-2xl p-8 text-white text-center mb-8">
            <GraduationCap size={48} className="mx-auto mb-4" weight="fill" />
            <h2 className="font-heading text-2xl font-bold mb-2">Dein Niveau: {determinedLevel}</h2>
            <p className="text-white/90">Basierend auf deinem Verständnis empfehlen wir dir den {determinedLevel}-Kurs.</p>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] overflow-hidden">
          <div className="bg-gradient-to-br from-[#AB1C24] to-[#8A151C] p-6 text-white text-center">
            <img src="/brand/hca-mark-256.png" alt="Habla" className="w-24 h-24 object-contain mx-auto mb-3 drop-shadow-lg" />
            <Wordmark size="lg" tone="light" className="mb-1 justify-center" />
            <p className="text-white/80">Dein Weg zum fließenden Spanisch</p>
          </div>
          <div className="p-6">
            {couponFree ? (
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-2xl text-[#78716C] line-through">100€</span>
                  <span className="text-5xl font-bold text-[#16A34A]">0€</span>
                </div>
                <span className="inline-block mt-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Komplett kostenlos!
                </span>
              </div>
            ) : couponApplied ? (
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-2xl text-[#78716C] line-through">100€</span>
                  <span className="text-5xl font-bold text-[#AB1C24]">60€</span>
                  <span className="text-2xl text-[#57534E]">/Monat</span>
                </div>
                <span className="inline-block mt-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Rabatt angewendet!
                </span>
              </div>
            ) : (
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-[#292524]">100€</span>
                <span className="text-2xl text-[#57534E]">/Monat</span>
              </div>
            )}
            <ul className="space-y-3 mb-6">
              {['Wöchentlicher Live-Kurs mit Alberto','KI-Lehrerin für Konversation','Personalisierte Lernanalyse','Community, Buddies & Challenges','Hörverstehen & Videoserie'].map((f,i)=>(
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#AB1C24]/10 flex items-center justify-center">
                    <Check size={14} className="text-[#AB1C24]" weight="bold" />
                  </div>
                  <span className="text-[#57534E]">{f}</span>
                </li>
              ))}
            </ul>

            {/* Coupon input — auto-applies on type */}
            {!couponApplied && (
              <div className="mb-4">
                <div className="relative">
                  <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
                  <Input
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value); applyCoupon(e.target.value); }}
                    placeholder="Gutscheincode"
                    className="pl-9 bg-white border-stone-300 text-sm"
                    data-testid="placement-coupon-input"
                  />
                </div>
                {couponError && <p className="text-red-500 text-xs mt-1.5">{couponError}</p>}
              </div>
            )}
            {couponApplied && (
              <div className="mb-4 bg-[#AB1C24]/5 border border-[#AB1C24]/20 rounded-xl p-3 text-left" data-testid="placement-coupon-applied">
                <div className="flex items-start gap-2">
                  <Heart size={18} className="text-[#AB1C24] flex-shrink-0 mt-0.5" weight="fill" />
                  <div>
                    {couponFree ? (
                      <>
                        <p className="font-semibold text-[#AB1C24] text-sm">Du bist ein Auserwählter!</p>
                        <p className="text-[#57534E] text-xs">100% Rabatt — kostenloser Zugang.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-[#AB1C24] text-sm">Rabatt freigeschaltet!</p>
                        <p className="text-[#57534E] text-xs">Lebenslanger Rabatt: 60€/Monat statt 100€.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!user && (
              <div className="space-y-3 mb-4" data-testid="guest-checkout-form">
                <Input placeholder="Dein Name" value={checkoutName}
                  onChange={e => { setCheckoutName(e.target.value); setCheckoutError(''); }}
                  className="py-5 rounded-xl" data-testid="guest-name-input" />
                <Input placeholder="Deine E-Mail-Adresse" type="email" value={checkoutEmail}
                  onChange={e => { setCheckoutEmail(e.target.value); setCheckoutError(''); }}
                  className="py-5 rounded-xl" data-testid="guest-email-input" />
                <div className="relative">
                  <Input placeholder="Passwort (min. 6 Zeichen)" type={showCheckoutPassword ? 'text' : 'password'} value={checkoutPassword}
                    onChange={e => { setCheckoutPassword(e.target.value); setCheckoutError(''); }}
                    className="py-5 pr-12 rounded-xl" data-testid="guest-password-input" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowCheckoutPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#AB1C24]"
                    data-testid="toggle-password-visibility" aria-label="Passwort anzeigen">
                    {showCheckoutPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {checkoutError && <p className="text-red-500 text-sm" data-testid="guest-checkout-error">{checkoutError}</p>}
              </div>
            )}
            <Button onClick={handleSubscribe} disabled={processingPayment}
              className="w-full bg-[#AB1C24] hover:bg-[#8A151C] py-6 text-lg rounded-xl" data-testid="subscribe-btn">
              {processingPayment ? <SpinnerGap size={24} className="animate-spin mr-2" /> : <CreditCard size={24} className="mr-2" />}
              {couponFree
                ? 'Kostenlos freischalten'
                : couponApplied
                  ? 'Jetzt für 60€/Monat starten'
                  : (user ? 'Jetzt abonnieren' : 'Zur Zahlung — 100€/Monat')}
            </Button>

            {/* Abo-Paket Selector — appears BELOW the one-off CTA. The
                visitor picks one of three monthly recurring tiers. Pricing
                rewards longer commitments (1m=100€, 3m=80€, 6m=60€) but
                Stripe still bills monthly so cancellation is friction-free.
                Hidden when a 100% free coupon is active (no point showing
                paid options if access is already free). */}
            {!couponFree && (
              <div className="mt-3" data-testid="subscription-toggle-wrap">
                <button
                  type="button"
                  onClick={() => {
                    const next = !showSubPlans;
                    setShowSubPlans(next);
                    if (next && typeof window !== 'undefined' && window.fbq) {
                      try {
                        window.fbq('track', 'ViewContent', {
                          content_type: 'subscription_plans',
                          content_name: 'Einstufungstest — Dauerabo Toggle',
                          content_category: 'subscription',
                        });
                      } catch { /* ignore */ }
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#292524] bg-white border-2 border-[#D6D3D1] hover:border-[#AB1C24] hover:text-[#AB1C24] rounded-xl transition-colors"
                  data-testid="open-subscription-btn"
                >
                  <Crown size={18} weight="fill" className="text-amber-500" />
                  Lieber ein Dauerabo abschließen?
                  <CaretDown size={14} className={`transition-transform ${showSubPlans ? 'rotate-180' : ''}`} />
                </button>
                {showSubPlans && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="subscription-plans-grid">
                    {[
                      { id: 'sub_1m', label: '1 Monat',  perMonth: 100, savings: null,                   highlight: false },
                      { id: 'sub_3m', label: '3 Monate', perMonth: 80,  savings: '20% günstiger',         highlight: true  },
                      { id: 'sub_6m', label: '6 Monate', perMonth: 60,  savings: '40% günstiger',         highlight: false },
                    ].map(p => (
                      <button
                        key={p.id}
                        onClick={() => handleSubscribeRecurring(p.id)}
                        disabled={processingPayment}
                        className={`relative text-left p-3 rounded-xl border-2 transition-all disabled:opacity-50 ${
                          p.highlight
                            ? 'border-[#AB1C24] bg-gradient-to-br from-red-50 to-white hover:shadow-md'
                            : 'border-stone-200 bg-[#FAF9F6] hover:border-[#AB1C24]'
                        }`}
                        data-testid={`placement-sub-${p.id}-btn`}
                      >
                        {p.highlight && (
                          <span className="absolute -top-2 right-2 text-[9px] font-bold bg-[#AB1C24] text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                            Beliebt
                          </span>
                        )}
                        <p className="font-heading font-bold text-[#292524] text-sm">{p.label}</p>
                        <p className="mt-0.5 text-xl font-bold text-[#AB1C24]">
                          {p.perMonth}€<span className="text-xs text-[#78716C] font-normal">/Monat</span>
                        </p>
                        {p.savings && (
                          <p className="text-[10px] text-emerald-700 font-semibold">{p.savings}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {showSubPlans && (
                  <p className="text-[11px] text-[#A8A29E] mt-2 text-center px-2">
                    Automatische monatliche Verlängerung · jederzeit aus dem Profil kündbar · sichere Zahlung über Stripe
                  </p>
                )}
                {showSubPlans && couponApplied && !couponFree && (
                  <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-2.5 flex items-start gap-2" data-testid="placement-coupon-sub-warning">
                    <span className="text-amber-600 text-base leading-none">⚠️</span>
                    <p className="text-[11px] text-amber-800 leading-snug">
                      <strong>Hinweis:</strong> Dein Rabatt-Code gilt nur für die Einmalzahlung oben.
                      Abonnements behalten den vollen Preis.
                    </p>
                  </div>
                )}
              </div>
            )}
            <p className="text-center text-sm text-[#78716C] mt-4">
              {couponFree
                ? 'Du erhältst deine Zugangsdaten sofort per E-Mail. Keine Zahlungsdaten erforderlich.'
                : (user ? 'Jederzeit kündbar. Sichere Zahlung via Stripe' : 'Nach der Zahlung erhältst du deine Zugangsdaten per E-Mail. Jederzeit kündbar.')}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-6 mt-6">
          {user && (
            <button onClick={() => navigate('/dashboard')} className="text-[#78716C] hover:text-[#AB1C24] text-sm underline">
              Zurück zum Dashboard
            </button>
          )}
          <Link to="/"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stone-300 bg-white text-[#57534E] text-sm font-medium transition-all hover:border-[#AB1C24] hover:text-[#AB1C24] hover:bg-[#AB1C24]/5 hover:shadow-sm">
            <House size={14} /> Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );

  // Test View
  const level = LEVEL_ORDER[currentLevel];
  const levelContent = content?.[level];

  return (
    <div className="min-h-screen canvas-spain py-12 px-4" data-testid="placement-test-page">
      <div className="max-w-lg mx-auto">
        {/* Header with back to landing */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 bg-white text-[#57534E] text-sm font-medium transition-all hover:border-[#AB1C24] hover:text-[#AB1C24] hover:bg-[#AB1C24]/5 hover:shadow-sm"
            data-testid="back-to-landing">
            <ArrowLeft size={16} /> Zur Startseite
          </Link>
          <button onClick={() => { setShowPricing(true); setTestComplete(true); }}
            className="px-3.5 py-2 rounded-full border border-stone-300 bg-white text-[#57534E] text-sm font-medium transition-all hover:border-[#AB1C24] hover:text-[#AB1C24] hover:bg-[#AB1C24]/5 hover:shadow-sm"
            data-testid="skip-test-btn">
            Test überspringen
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          {/* Header icon + title switch between Lesen (A1/A2) and Audio
              (B1/B2/C1) so the visual matches the actual task. Previously
              the icon was always a speaker, which confused students on the
              A1/A2 reading levels. */}
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-[#AB1C24] to-[#8A151C] flex items-center justify-center">
            {currentLevel < 2 ? (
              <BookOpen size={28} className="text-white" weight="fill" />
            ) : (
              <SpeakerHigh size={28} className="text-white" weight="fill" />
            )}
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#292524]" data-testid="placement-test-title">
            {currentLevel < 2 ? 'Einstufungstest — Lesen' : 'Einstufungstest mit Audio'}
          </h1>
          <p className="text-[#57534E] text-sm mt-1">In weniger als 30 Sekunden</p>
        </div>

        {/* Level Navigation Bar */}
        <div className="flex items-center justify-center gap-2 mb-3" data-testid="level-nav">
          {LEVEL_ORDER.map((lvl, idx) => {
            const isActive = idx === currentLevel;
            const isDone = results.some(r => r.level === lvl);
            const result = results.find(r => r.level === lvl);
            return (
              <button
                key={lvl}
                onClick={() => goToLevel(idx)}
                className={`relative flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl transition-all ${
                  isActive
                    ? 'bg-[#AB1C24] text-white shadow-lg shadow-[#AB1C24]/25 scale-105'
                    : isDone
                      ? result?.understood
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-white text-[#78716C] border border-[#D6D3D1] hover:border-[#AB1C24]/30 opacity-70 hover:opacity-100'
                }`}
                data-testid={`level-btn-${lvl}`}
              >
                <span className="text-xs font-medium">{idx < 2 ? 'Lesen' : 'Audio'}</span>
                <span className="font-bold text-lg">{lvl}</span>
                {isDone && (
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center bg-white shadow-sm">
                    {result?.understood
                      ? <Check size={12} className="text-green-600" weight="bold" />
                      : <X size={12} className="text-red-500" weight="bold" />
                    }
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Per-level "Buy this level" row */}
        <div className="grid grid-cols-5 gap-2 mb-8" data-testid="level-buy-row">
          {LEVEL_ORDER.map((lvl) => (
            <button
              key={`buy-${lvl}`}
              onClick={() => buyLevel(lvl)}
              className="text-[11px] font-medium text-[#AB1C24] hover:bg-[#AB1C24] hover:text-white border border-[#AB1C24]/30 rounded-lg py-1.5 transition-colors flex items-center justify-center gap-1"
              data-testid={`buy-level-${lvl}`}
            >
              <CreditCard size={12} weight="fill" />
              {lvl} kaufen
            </button>
          ))}
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8 mb-6" data-testid="content-card">
          <p className="text-center text-[#57534E] text-sm mb-5">{levelContent?.description}</p>

          {currentLevel < 2 ? (
            <>
              <div className="p-5 bg-[#F5F5F4] rounded-xl">
                <p className="text-[#292524] text-lg leading-relaxed text-center">"{levelContent?.text}"</p>
              </div>
              <p className="text-center text-sm text-[#78716C] mt-4">Lies den Text und entscheide, ob du ihn verstehst.</p>
            </>
          ) : (
            <>
              <button
                onClick={playAudio}
                disabled={audioLoading}
                className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center transition-all ${
                  isPlaying ? 'bg-[#AB1C24] scale-110' : 'bg-gradient-to-br from-[#AB1C24] to-[#8A151C] hover:scale-105'
                }`}
                data-testid="play-audio-btn"
              >
                {audioLoading ? (
                  <SpinnerGap size={36} className="text-white animate-spin" />
                ) : isPlaying ? (
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="w-1 bg-white rounded-full waveform-bar" style={{ height: '20px' }} />
                    ))}
                  </div>
                ) : (
                  <Play size={40} className="text-white ml-1" weight="fill" />
                )}
              </button>
              <p className="text-center text-sm text-[#78716C] mt-4">
                {audioLoading ? 'Generando audio...' : isPlaying ? 'Wird abgespielt...' : 'Klicke zum Abspielen'}
              </p>
              {startTime && !audioLoading && (
                <div className="mt-5 p-4 bg-[#F5F5F4] rounded-xl">
                  <p className="text-xs text-[#78716C] mb-1 text-center uppercase tracking-wide">Mitlesen</p>
                  <p className="text-sm text-[#292524] leading-relaxed text-center">"{levelContent?.text}"</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Navigation + Response Buttons */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Button onClick={() => handleResponse(false)} variant="outline"
              className="py-6 rounded-xl border-2 border-red-200 hover:bg-red-50 hover:border-red-300" data-testid="not-understood-btn">
              <X size={24} className="mr-2 text-red-500" weight="bold" />
              <span className="text-red-700">Nicht verstanden</span>
            </Button>
            <Button onClick={() => handleResponse(true)}
              className="py-6 rounded-xl bg-[#6B8E23] hover:bg-[#5a7a1e]" data-testid="understood-btn">
              <Check size={24} className="mr-2" weight="bold" />
              Verstanden!
            </Button>
          </div>

          {/* Back button */}
          {currentLevel > 0 && (
            <button onClick={() => goToLevel(currentLevel - 1)}
              className="w-full flex items-center justify-center gap-2 text-[#78716C] hover:text-[#AB1C24] text-sm py-2 transition-colors" data-testid="back-level-btn">
              <ArrowLeft size={16} /> Zurück zu {LEVEL_ORDER[currentLevel - 1]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlacementTestPage;
